// BuildingModal.jsx
import { useState, useMemo } from 'react';
import {
  Alert,
  View,
  Flex,
  TextField,
  TextAreaField,
  Button,
} from '@aws-amplify/ui-react';
import SimpleDialog from '../common/SimpleDialog';
import { normalizeCode } from '../../utils/codeUtils';
import { normalizeGraphQLError } from '../../utils/graphqlErrors';
import { getFieldError } from '../../utils/formErrors';

export default function BuildingModal({
  open,
  onClose,
  onCreated,
  createBuildingFn,     // caller passes the actual GraphQL call
  buildingProperty
}) {
  const maxNameLen = 80, maxNotesLen = 500;

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // frontend “touched” tracking
  const [touched, setTouched] = useState({
    code: false,
    name: false,
    notes: false,
  });

  const validation = useMemo(() => {
    const v = {};

    const cleanCode = normalizeCode(code);
    const cleanName = name.trim();
    const cleanNotes = notes.trim();

    if (!cleanCode) {
      v.code = 'Code is required.';
    } else if (cleanCode.length < 1 || cleanCode.length > 32) {
      v.code = 'Code must be 1–32 chars.';
    }

    if (cleanName && cleanName.length > maxNameLen) {
      v.name = `Keep name under ${maxNameLen} characters.`;
    }

    if ((cleanNotes || '').length > maxNotesLen) {
      v.notes = `Notes must be ≤ ${maxNotesLen} chars.`;
    }

    return v;
  }, [code, name, notes]);

  const hasErrors = Object.values(validation).some(Boolean);

  async function handleSubmit(e) {
    e.preventDefault();

    setTouched({ code: true, name: true, notes: true });

    if (hasErrors) return;

    // Will set the id in the backend. I want to start doing UUID as id rather than propId#buildingCode
    setSubmitting(true);

    const payload = {
      code: normalizeCode(code),
      name: name.trim() || null,
      notes: notes.trim() || null
    };

    try {
      const input = {propertyId: buildingProperty?.id, ...payload};
      const b = await createBuildingFn(input);

      // Reset local state
      setCode('');
      setName('');
      setNotes('');
      setErrors({});
      setTouched({ name: false, code: false, notes: false });
      onCreated?.(b); // let parent select it in Add Device form
      onClose?.();
    } catch (err) {
      console.error('Failed to create building:', err);

      const mapped = normalizeGraphQLError(err);
      setErrors(mapped);
    } finally {
      setSubmitting(false);
    }
  }

  const codeError = getFieldError({
    field: "code",
    touched,
    validation,
    errors
  });

  const nameError = getFieldError({
    field: "name",
    touched,
    validation,
    errors
  });

  const notesError = getFieldError({
    field: "notes",
    touched,
    validation,
    errors
  });
  return (
    <SimpleDialog open={open} onClose={onClose} title={`New Building for ${buildingProperty?.name ?? 'selected property'}`}>
      <View as="form" onSubmit={handleSubmit}>
        <Flex direction="column" gap="0.75rem">
          {errors.global && (
            <Alert
              variation="error"
              isDismissible={true}
              onDismiss={() => setErrors((e) => ({ ...e, global: undefined }))}
              heading="Couldn't create building"
            >
              {errors.global}
            </Alert>
          )}
          <TextField
            label="Code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              if (errors.code) setErrors(prev => ({ ...prev, code: undefined, global: undefined }));
            }}
            onBlur={() => {
              setTouched((t) => ({ ...t, code: true }));
              setCode((s) => normalizeCode(s));
            }}
            placeholder="e.g., B"
            descriptiveText="Short and unique within the selected property."
            hasError={!!codeError}
            errorMessage={codeError || undefined}
            isDisabled={submitting}
            autoFocus
            isRequired
            autoComplete="off"
          />

          <TextField
            label="Name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., North Building"
            onBlur={() =>
              setTouched((t) => ({ ...t, name: true }))
            }
            hasError={!!nameError}
            errorMessage={nameError || undefined}
            isDisabled={submitting}
            autoComplete="off"
          />

          <TextAreaField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() =>
              setTouched((t) => ({ ...t, notes: true }))
            }
            hasError={!!notesError}
            errorMessage={notesError || undefined}
            rows={3}
            isDisabled={submitting}
            autoComplete="off"
          />

          <Flex justifyContent="flex-end" gap="0.5rem" marginTop="0.5rem">
            <Button type="button" onClick={onClose} variation="link" isDisabled={submitting}>Cancel</Button>
            <Button type="submit" variation="primary" isLoading={submitting}>Create</Button>
          </Flex>
        </Flex>
      </View>
    </SimpleDialog>
  );
}
